// Turns the symbol endpoint's response into options for the device pickers.
//
// That endpoint returns three things:
//   symbols      the legacy crypto-only list, kept so an older backend still works
//   markets      every market, each with an `enabled` flag set by the admin switch
//   instruments  the instruments belonging to the markets that are switched on
//
// Only switched-on markets ever reach a picker. With crypto alone switched on the
// picker stays a single flat list of coin names, exactly as it was before the
// other markets existed: a lone group heading would be noise.

const titleCase = (value) => {
    const s = String(value || '').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

const optionFor = (instrument) => ({
    label: instrument.label || instrument.baseAsset || instrument.providerSymbol || instrument.id,
    value: instrument.id,
    market: instrument.market || 'crypto',
});

/**
 * Group the available instruments by market, in the order the endpoint lists the
 * markets, so crypto stays first. Markets with nothing in them are left out.
 * Falls back to the legacy crypto list when the endpoint predates instruments.
 */
export const buildInstrumentGroups = (symbolData) => {
    const data = symbolData || {};
    const instruments = Array.isArray(data.instruments) ? data.instruments : [];

    if (!instruments.length) {
        const legacy = Array.isArray(data.symbols) ? data.symbols : [];
        if (!legacy.length) return [];
        return [{
            id: 'crypto',
            label: 'Crypto',
            options: legacy.map((s) => ({ label: s.baseAsset || s.symbol, value: s.symbol, market: 'crypto' })),
        }];
    }

    const markets = Array.isArray(data.markets) ? data.markets : [];
    const order = new Map(markets.map((m, i) => [m.id, i]));
    const labels = new Map(markets.map((m) => [m.id, m.label]));

    const byMarket = new Map();
    for (const instrument of instruments) {
        if (!instrument || !instrument.id) continue;
        const marketId = instrument.market || 'crypto';
        const bucket = byMarket.get(marketId);
        if (bucket) bucket.push(instrument); else byMarket.set(marketId, [instrument]);
    }

    return [...byMarket.entries()]
        .sort((a, b) => {
            const ai = order.has(a[0]) ? order.get(a[0]) : Number.MAX_SAFE_INTEGER;
            const bi = order.has(b[0]) ? order.get(b[0]) : Number.MAX_SAFE_INTEGER;
            return ai - bi;
        })
        .map(([marketId, list]) => ({
            id: marketId,
            label: labels.get(marketId) || titleCase(marketId),
            options: list.map(optionFor),
        }));
};

/**
 * What to hand react-select. One market means a flat list and no heading; more
 * than one means grouped options with a heading per market.
 */
export const toSelectOptions = (groups) => {
    const list = groups || [];
    if (list.length <= 1) return list.length ? list[0].options : [];
    return list.map((g) => ({ label: g.label, options: g.options }));
};

/** Every option across every group, flattened. */
export const allOptions = (groups) => (groups || []).flatMap((g) => g.options || []);

/**
 * The option for a stored device value, looked up across every group.
 * Returns undefined when the device points at something no longer offered, which
 * leaves the picker blank rather than showing a wrong selection.
 */
export const findInstrumentOption = (groups, value) => {
    if (value === null || value === undefined || value === '') return undefined;
    return allOptions(groups).find((o) => o.value === value);
};

/** The options for a list of stored values, in the order the device stored them. */
export const findInstrumentOptions = (groups, values) => {
    const wanted = Array.isArray(values) ? values : [];
    const options = allOptions(groups);
    return wanted
        .map((v) => options.find((o) => o.value === v))
        .filter(Boolean);
};

/** How many instruments are on offer in total. */
export const countInstruments = (groups) => allOptions(groups).length;

/** True when more than one market is on offer, so the picker will show headings. */
export const isGrouped = (groups) => (groups || []).length > 1;

/**
 * The markets worth offering as a choice, with how many instruments each holds.
 * A market with nothing in it never reaches here, because the groups leave it out.
 */
export const marketChoices = (groups) => (groups || []).map((g) => ({
    id: g.id,
    label: g.label,
    count: (g.options || []).length,
}));

/** Just the instruments of one market, for a picker already scoped to it. */
export const optionsForMarket = (groups, marketId) => {
    const group = (groups || []).find((g) => g.id === marketId);
    return group ? group.options : [];
};

/**
 * Which market a stored device value belongs to, so the market selector opens on
 * the one the device is already using rather than resetting it to the first.
 */
export const marketOfValue = (groups, value) => {
    const option = findInstrumentOption(groups, value);
    return option ? option.market : null;
};

/**
 * The market a picker should start on: the one the device is already set to, or
 * failing that the first on offer.
 */
export const defaultMarket = (groups, value) => {
    const current = marketOfValue(groups, value);
    if (current) return current;
    const first = (groups || [])[0];
    return first ? first.id : null;
};
